import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  useCreateNotificationChannelTopicMutation,
  useNotificationChannelTopicQuery,
  useSendTestNotificationMutation,
  useUpdateNotificationChannelTopicMutation,
} from "../NotificationRules.g";

import { Box } from "@design-system/Box/Box";
import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { TextField } from "@design-system/TextField/TextField";
import { Select } from "@design-system/Select/Select";
import { InfiniteClientSelect } from "@components/Selects/InfiniteClientSelect";
import { Text } from "@design-system/Text/Text";

type Props = {
  isOpen: boolean;
  ruleId?: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const RuleSlideInPanel = ({ isOpen, ruleId, onClose, onSuccess }: Props) => {
  const isEditing = !!ruleId;
  const { data } = useNotificationChannelTopicQuery(
    { id: ruleId! },
    { enabled: isEditing }
  );

  const { mutateAsync: createRule } = useCreateNotificationChannelTopicMutation();
  const { mutateAsync: updateRule } = useUpdateNotificationChannelTopicMutation();
  const { mutateAsync: sendTest } = useSendTestNotificationMutation();

  const [name, setName] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [channelType, setChannelType] = useState("EMAIL");
  const [emails, setEmails] = useState("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [applicationReviewStates, setApplicationReviewStates] = useState<string[]>([]);

  // Selected Clients
  const [selectedClients, setSelectedClients] = useState<Array<{ id: string; label: string }>>([]);
  const [currentClientSelect, setCurrentClientSelect] = useState<string | undefined>();

  useEffect(() => {
    if (data?.notificationChannelTopic) {
      const topic = data.notificationChannelTopic;
      setName(topic.name);
      setIsEnabled(topic.notificationChannel?.isEnabled ?? true);
      setChannelType(topic.notificationChannel?.channelType ?? "EMAIL");
      setEventTypes((topic.types as Array<{ notificationType: string }>)?.map((t) => t.notificationType) ?? []);
      setApplicationReviewStates(topic.applicationReviewStates ?? []);
      setSelectedClients(
        (topic.clients as Array<{ client: { id: string; name: string } }>)?.map((c) => ({
          id: c.client.id,
          label: c.client.name,
        })) ?? []
      );
      setEmails((topic.emailRecipients as Array<{ emailAddress: string }>)?.map((r) => r.emailAddress).join(", ") ?? "");
    }
  }, [data]);

  const handleSave = async () => {
    const emailList = emails
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    const clientIds = selectedClients.map((c) => c.id);

    try {
      if (isEditing) {
        await updateRule({
          input: {
            id: ruleId!,
            name,
            channelType,
            isEnabled,
            notificationTypes: eventTypes,
            clientIds,
            emailAddresses: emailList,
            applicationReviewStates,
          },
        });
      } else {
        await createRule({
          input: {
            name,
            channelType,
            isEnabled,
            notificationTypes: eventTypes,
            clientIds,
            emailAddresses: emailList,
            applicationReviewStates,
          },
        });
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save rule");
    }
  };

  const handleSendTest = async () => {
    const emailList = emails
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (emailList.length === 0) {
      toast.info("Please enter at least one email address first.");
      return;
    }

    try {
      const response = await sendTest({
        input: {
          ruleName: name || "Draft Rule",
          emailAddresses: emailList,
        },
      });
      
      if (response.sendTestNotification?.errors && response.sendTestNotification.errors.length > 0) {
        toast.error(response.sendTestNotification.errors[0]?.message || "Failed to send test email");
        return;
      }
      
      toast.info("Test email sent successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send test email");
    }
  };

  const EVENT_OPTIONS = [
    { id: "SAAS_DISCOVERED", label: "SaaS Discovered" },
    { id: "SHARED_LOGIN", label: "Shared Login" },
    { id: "FILE_UPLOAD", label: "File Upload" },
  ];

  const APPLICATION_REVIEW_STATE_OPTIONS = [
    { id: "DISCOVERED", label: "Discovered" },
    { id: "EVALUATING", label: "Evaluating" },
    { id: "APPROVED", label: "Approved" },
    { id: "UNAPPROVED", label: "Unapproved" },
  ];

  return (
    <SlideInPanel
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      onApply={handleSave}
      applyButtonText={isEditing ? "Save Changes" : "Create Rule"}
      title={isEditing ? "Edit Rule" : "Create Rule"}
      extraButtonText={channelType === "EMAIL" ? "Send Test Notification" : undefined}
      onExtraButtonClick={channelType === "EMAIL" ? handleSendTest : undefined}
    >
      <Box direction="column" gap={20}>
        <FieldGroup label="Rule Name">
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. High Priority Alerts"
        />
      </FieldGroup>

      <FieldGroup label="Status">
        <Select
          value={isEnabled ? "ACTIVE" : "DISABLED"}
          onChange={(val) => setIsEnabled(val === "ACTIVE")}
          options={[
            { id: "ACTIVE", label: "Active" },
            { id: "DISABLED", label: "Disabled" },
          ]}
        />
      </FieldGroup>

      <FieldGroup label="Channel">
        <Select
          value={channelType}
          onChange={(val) => setChannelType(val as string)}
          options={[
            { id: "EMAIL", label: "Email" },
            { id: "SLACK", label: "Slack (Coming Soon)" },
            { id: "AUTOTASK", label: "Autotask (Coming Soon)" },
          ]}
        />
      </FieldGroup>

      {channelType === "EMAIL" && (
        <FieldGroup label="Email Recipients (comma-separated)">
          <TextField
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="admin@example.com, support@example.com"
          />
        </FieldGroup>
      )}

      <FieldGroup label="Event Types">
        <Select
          value={eventTypes}
          onChange={(val) => setEventTypes(val as string[])}
          options={EVENT_OPTIONS}
        />
        <Text size={12} color="textSecondary">
          If no events are selected, this rule applies to all events.
        </Text>
      </FieldGroup>

      <FieldGroup label="Application Scopes">
        <Select
          value={applicationReviewStates}
          onChange={(val) => setApplicationReviewStates(val as string[])}
          options={APPLICATION_REVIEW_STATE_OPTIONS}
        />
        <Text size={12} color="textSecondary">
          If no application scopes are selected, this rule applies to all application states. This filter is only evaluated for notifications related to an application.
        </Text>
      </FieldGroup>

      <FieldGroup label="Specific Clients">
        <InfiniteClientSelect
          value={currentClientSelect}
          onChange={(val, option) => {
            if (val && option && !selectedClients.find((c) => c.id === val)) {
              setSelectedClients([
                ...selectedClients,
                { id: val, label: option.label },
              ]);
            }
            setCurrentClientSelect(undefined);
          }}
          placeholder={selectedClients.length === 0 ? "Select a client..." : ""}
          leftSlot={
            selectedClients.length > 0 ? (
              <>
                {selectedClients.map((c) => (
                  <Select.TagValue
                    key={c.id}
                    options={selectedClients}
                    value={c.id}
                    onRemove={(id) => setSelectedClients(selectedClients.filter((sc) => sc.id !== id))}
                  />
                ))}
              </>
            ) : undefined
          }
          wrap={true}
        />
        <Text size={12} color="textSecondary">
          If no clients are added, this rule applies to all clients.
        </Text>
      </FieldGroup>
      </Box>
    </SlideInPanel>
  );
};
